import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Forgot your password?" subtitle="Enter your account email and we'll send you a reset link.">
      {sent ? (
        <p className="rounded-md bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="forgot-password-sent">If an account exists for {email}, a reset link is on its way. It expires in 1 hour. Check your spam folder too.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
          <Field id="femail" label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-password-email-input" />
          {error && <p className="text-sm text-red-600" data-testid="forgot-password-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800" data-testid="forgot-password-submit-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send reset link</Button>
        </form>
      )}
      <p className="mt-6 text-sm"><Link to="/login" className="font-semibold text-blue-700 hover:underline" data-testid="back-to-login-link">Back to log in</Link></p>
    </Shell>
  );
};

export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setError("Passwords don't match");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      nav("/login?reset=1");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Set a new password" subtitle="Choose a password with at least 8 characters.">
      {!token ? <p className="text-sm text-red-600" data-testid="reset-password-missing-token">This link is missing its reset code. <Link to="/forgot-password" className="underline">Request a new link</Link>.</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
          <Field id="np" label="New password" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
          <Field id="np2" label="Confirm new password" type="password" minLength={8} required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm-input" />
          {error && <p className="text-sm text-red-600" data-testid="reset-password-error">{error} {error.includes("expired") && <Link to="/forgot-password" className="underline">Request a new link</Link>}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800" data-testid="reset-password-submit-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save new password</Button>
        </form>
      )}
    </Shell>
  );
};

const Shell = ({ title, subtitle, children }) => (
  <div className="grid-lines flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-16">
    <div className="fade-up w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
      <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
      <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      <div className="mt-8">{children}</div>
    </div>
  </div>
);

const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} {...props} />
  </div>
);

const useNext = (user) => {
  const [params] = useSearchParams();
  return params.get("next") || (user?.role === "admin" ? "/admin" : "/dashboard");
};

export const LoginPage = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(form.email, form.password);
      nav(params.get("next") || (u.role === "admin" ? "/admin" : "/dashboard"));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Welcome back" subtitle="Log in to track your SR22 filing and policies.">
      {params.get("reset") && <p className="mb-4 rounded-md bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="login-reset-success">Password updated. Log in with your new password.</p>}
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field id="email" label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-login-email-input" />
        <Field id="password" label="Password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="customer-login-password-input" />
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <div className="text-right"><Link to="/forgot-password" className="text-sm text-blue-700 hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>
        <Button type="submit" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 hover:text-white" data-testid="customer-login-submit-btn">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Log in
        </Button>
      </form>
      <p className="mt-6 text-sm text-slate-500">
        New here? <Link to={`/register${params.get("next") ? `?next=${encodeURIComponent(params.get("next"))}` : ""}`} className="font-semibold text-blue-700 hover:underline" data-testid="go-register-link">Create an account</Link>
      </p>
    </Shell>
  );
};

export const RegisterPage = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = useNext(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register(form);
      nav(next);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Create your account" subtitle="Save your quote, pay securely and track your filing.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field id="name" label="Full name" required value={form.name} onChange={set("name")} data-testid="register-name-input" />
        <Field id="remail" label="Email" type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" />
        <Field id="phone" label="Phone" type="tel" value={form.phone} onChange={set("phone")} data-testid="register-phone-input" />
        <Field id="rpassword" label="Password (8+ characters)" type="password" minLength={8} required value={form.password} onChange={set("password")} data-testid="register-password-input" />
        {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 hover:text-white" data-testid="register-submit-btn">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
        </Button>
      </form>
      <p className="mt-6 text-sm text-slate-500">
        Already have an account? <Link to={`/login${params.get("next") ? `?next=${encodeURIComponent(params.get("next"))}` : ""}`} className="font-semibold text-blue-700 hover:underline" data-testid="go-login-link">Log in</Link>
      </p>
    </Shell>
  );
};
